import { useContext, ChangeEvent, useState, useEffect } from 'react'
import { Box, Hidden, useTheme, styledComponent } from '@hermes/web-components'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { useTimeout } from '../../../../hooks/useTimeout'
import { Sorting } from '../../common/types'
import WebView from './WebView'
import MobileView from './MobileView'
import ContentTopContainer from '../../../ContentTopContainer'

const MobileContentTopContainer = styledComponent(ContentTopContainer)(({ theme }) => ({
  ...(theme?.custom?.SearchBar?.mobile?.container ?? {})
}))

const SearchBar = ({
  renderSearchFilters,
  sorting
}: {
  renderSearchFilters: (...p: any) => JSX.Element
  sorting: Sorting[]
}) => {
  const theme = useTheme()
  const {
    changeSearchType,
    changeSearch,
    changeLocationSearch,
    loadCurrentLocationByLatLon,
    state: { filters }
  } = useContext(SearchProfilesPageContext)

  const [isSearchFieldFocused, setIsSearchFieldFocused] = useState(false)
  const [isLocationFieldFocused, setIsLocationFieldFocused] = useState(false)

  const timeout = useTimeout()

  const onChangeSearch = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    timeout(() => changeSearch(ev.target.value), 700)

  const onChangeLocationSearch = (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    timeout(() => {
      changeLocationSearch(ev.target.value, filters.language)
    })

  const onSearchFieldFocus = () => timeout(() => setIsSearchFieldFocused(true))
  const onSearchFieldBlur = () =>
    timeout(() => {
      changeSearch('')
      setIsSearchFieldFocused(false)
    })

  const getYourLocation = () => {
    timeout(() => {
      navigator.geolocation.getCurrentPosition((position) => {
        loadCurrentLocationByLatLon(position.coords.latitude.toString(), position.coords.longitude.toString()).then(
          () => {
            setIsLocationFieldFocused(true)
          }
        )
      })
    })
  }

  useEffect(() => {
    changeSearch('')
    // todo: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box
      sx={{
        marginTop: { xs: '48px', sm: '64px' },
        zIndex: 99,
        width: '100%'
      }}
    >
      <Box component={Hidden} lgDown width="100%" implementation="css">
        <ContentTopContainer
          sx={{
            backgroundColor: 'background.default',
            py: { xs: 2, lg: 2 },
            ...(theme?.custom?.SearchBar ?? {})
          }}
          maxWidth="xl"
        >
          <WebView
            getYourLocation={getYourLocation}
            isSearchFieldFocused={isSearchFieldFocused}
            isLocationFieldFocused={isLocationFieldFocused}
            onChangeLocationSearch={onChangeLocationSearch}
            onChangeSearch={onChangeSearch}
            onChangeSearchType={changeSearchType}
            onSearchFieldBlur={onSearchFieldBlur}
            onSearchFieldFocus={onSearchFieldFocus}
            setIsSearchFieldFocused={setIsSearchFieldFocused}
            setIsLocationFieldFocused={setIsLocationFieldFocused}
          />
        </ContentTopContainer>
      </Box>

      <Box component={Hidden} width="100%" lgUp implementation="css">
        <MobileContentTopContainer maxWidth="xl">
          <Box component={Hidden} width="100%" lgUp implementation="css">
            <MobileView
              getYourLocation={getYourLocation}
              isSearchFieldFocused={isSearchFieldFocused}
              onChangeLocationSearch={onChangeLocationSearch}
              onChangeSearch={onChangeSearch}
              onChangeSearchType={changeSearchType}
              onSearchFieldBlur={onSearchFieldBlur}
              onSearchFieldFocus={onSearchFieldFocus}
              setIsSearchFieldFocused={setIsSearchFieldFocused}
              isLocationFieldFocused={isLocationFieldFocused}
              sorting={sorting}
              renderSearchFilters={renderSearchFilters}
              setIsLocationFieldFocused={setIsLocationFieldFocused}
            />
          </Box>
        </MobileContentTopContainer>
      </Box>
    </Box>
  )
}

export default SearchBar
